import {isNativeBridgeAvailable, NativeCamera, NativePermissions, ImageCompressionOptions} from '../components/PhotoNativeBridge';
import {Toast} from 'antd-mobile';
import {fileUpload} from "../../../utils/storageUtils";
import {base64ToFile, FILE_UPLOAD_FAILED_KEY} from "../../../services/DeliveryDataService";
import i18n from '../../../i18n';
import { t } from 'i18n';
import { GeoPosition } from './GeolocationService';

export type PositionType = 'label' | 'location' | 'houseNumber';
export type PhotoSource = 'camera' | 'gallery'; // 照片来源：拍照 or 相册
export type PhotoType = {
    url: string;
    objectKey: string;
    latitude?: number;
    longitude?: number;
    capturedAt?: number; // 拍照时间戳
    source?: PhotoSource; // 照片来源
};
export type PhotoUpdateCallback = (updateFn: (prev: Record<PositionType, PhotoType | undefined>) => Record<PositionType, PhotoType | undefined>) => void;

// 图片压缩选项接口
interface CompressionOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  enableResize: boolean;
}

class PhotoService {
  /**
   * 检查相机权限并请求
   * @returns Promise<boolean>
   */
  async checkAndRequestCameraPermission(): Promise<boolean> {
    // 首先检查权限
    const hasPermission = await NativePermissions.check('camera');
    if (hasPermission) {
      return true;
    }

    // 如果没有权限，请求权限
    return await NativePermissions.request('camera');
  }
  
  /**
   * 检查存储权限并请求
   * @returns Promise<boolean>
   */
  async checkAndRequestStoragePermission(): Promise<boolean> {
    if (!isNativeBridgeAvailable()) {
      return true; // 网页环境下默认返回true
    }
    
    // 首先检查权限
    const hasPermission = await NativePermissions.check('storage');
    if (hasPermission) {
      return true;
    }
    
    // 如果没有权限，请求权限
    return await NativePermissions.request('storage');
  }

  /**
   * 压缩图片（JavaScript实现，参考Android逻辑）
   * @param file 原始图片文件
   * @param options 压缩选项
   * @returns Promise<File> 压缩后的文件
   */
  async compressImage(file: File, options: CompressionOptions = {
    maxWidth: 1080,
    maxHeight: 1080,
    quality: 75,
    enableResize: true
  }): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          let { width, height } = img;

          // 记录压缩前的大小
          const originalSize = file.size;
          console.log(`[PhotoService] 压缩前图片大小: ${(originalSize / 1024).toFixed(1)} KB`);

          // 如果需要调整尺寸
          if (options.enableResize && (width > options.maxWidth || height > options.maxHeight)) {
            const widthRatio = options.maxWidth / width;
            const heightRatio = options.maxHeight / height;
            const scaleFactor = Math.min(widthRatio, heightRatio);

            width = Math.round(width * scaleFactor);
            height = Math.round(height * scaleFactor);

            console.log(`[PhotoService] 调整图片尺寸: ${img.width}x${img.height} -> ${width}x${height}`);
          }

          // 设置canvas尺寸
          canvas.width = width;
          canvas.height = height;

          // 绘制图片到canvas
          ctx?.drawImage(img, 0, 0, width, height);

          // 转换为blob
          canvas.toBlob((blob) => {
            if (blob) {
              // 创建新的File对象
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });

              // 记录压缩后的大小
              const compressedSize = compressedFile.size;
              console.log(`[PhotoService] 压缩后图片大小: ${(compressedSize / 1024).toFixed(1)} KB`);
              console.log(`[PhotoService] 压缩比例: ${((1 - compressedSize / originalSize) * 100).toFixed(1)}%`);

              resolve(compressedFile);
            } else {
              reject(new Error('图片压缩失败'));
            }
          }, 'image/jpeg', options.quality / 100);

        } catch (error) {
          console.error('[PhotoService] 图片压缩错误:', error);
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      // 读取文件
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error('文件读取失败'));
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 打开相册选择照片
   * @param compressionOptions 图片压缩选项（原生桥接使用，浏览器环境内部硬编码）
   * @returns Promise<File>
   */
  async selectFromGallery(compressionOptions?: ImageCompressionOptions): Promise<File> {
    // 如果有原生桥接，使用原生
    if (isNativeBridgeAvailable()) {
      try {
        // 检查存储权限
        const hasPermission = await this.checkAndRequestStoragePermission();
        if (!hasPermission) {
          throw new Error(t('pages.delivery.detail.photo.storagePermissionDenied'));
        }
        
        const base64Data = await NativeCamera.openGallery(compressionOptions);
        return base64ToFile(base64Data, `photo_${Date.now()}.jpg`);
      } catch (error) {
        console.error('Failed to use native gallery:', error);
        // 原生相册失败时，不尝试相机权限，直接使用文件选择器
      }
    }

    // 回退到网页文件选择器（不使用相机API）
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      // 不设置capture属性，避免iOS相机问题
      
      let isResolved = false;
      let timeoutId: NodeJS.Timeout;
      
      // 设置超时机制，防止iOS WKWebView无响应
      const timeout = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(new Error(t('pages.delivery.detail.photo.fileSelectionTimeout')));
        }
      }, 30000); // 30秒超时
      
      const cleanup = () => {
        clearTimeout(timeout);
        input.removeEventListener('change', handleChange);
        input.removeEventListener('cancel', handleCancel);
        input.removeEventListener('error', handleError);
        // 清理input元素
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };
      
      const handleChange = async (event: Event) => {
        if (isResolved) return;
        
        try {
          const files = (event.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            isResolved = true;
            cleanup();

            const originalFile = files[0];

            // 检查是否为浏览器环境，应用JavaScript压缩
            // 原生App会走原生桥接，不会到这里
            const isBrowser = !isNativeBridgeAvailable();
            console.log('[PhotoService] 原生桥接可用:', isNativeBridgeAvailable());
            console.log('[PhotoService] 是否为浏览器环境:', isBrowser);

            if (isBrowser) {
              console.log('[PhotoService] 浏览器环境，应用JavaScript压缩');
              const compressedFile = await this.compressImage(originalFile, {
                maxWidth: 1080,
                maxHeight: 1080,
                quality: 75,
                enableResize: true
              });
              resolve(compressedFile);
            } else {
              console.log('[PhotoService] 原生App环境，使用原始文件');
              resolve(originalFile);
            }
          } else {
            isResolved = true;
            cleanup();
            reject(new Error('No file selected'));
          }
        } catch (error) {
          isResolved = true;
          cleanup();
          reject(error);
        }
      };
      
      const handleCancel = () => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(t('pages.delivery.detail.photo.selectionCancelled')));
      };
      
      const handleError = (error: Event) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(`File selection error: ${error}`));
      };
      
      // 使用addEventListener而不是onchange，更可靠
      input.addEventListener('change', handleChange);
      input.addEventListener('cancel', handleCancel);
      input.addEventListener('error', handleError);
      
      // 添加input到DOM中，提高iOS兼容性
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      input.style.top = '-9999px';
      document.body.appendChild(input);
      
      try {
        input.click();
      } catch (error) {
        isResolved = true;
        cleanup();
        reject(new Error(`Failed to trigger file input: ${error}`));
      }
    });
  }

  /**
   * 打开相机拍照
   * @param useFrontCamera 是否使用前置相机
   * @param compressionOptions 图片压缩选项
   * @returns Promise<File>
   */
  async takePhoto(useFrontCamera: boolean = false, compressionOptions?: ImageCompressionOptions): Promise<File> {
    // 如果有原生桥接，使用原生相机
    if (isNativeBridgeAvailable()) {
      try {
        // 检查相机权限
        const cameraPermission = await this.checkAndRequestCameraPermission();
        if (!cameraPermission) {
          throw new Error(t('pages.delivery.detail.photo.cameraPermissionDenied'));
        }

        const base64Data = await NativeCamera.openCamera(useFrontCamera, compressionOptions);
        return base64ToFile(base64Data, `camera_${Date.now()}.jpg`);
      } catch (error) {
        console.error('Failed to use native camera:', error);
        throw error;
      }
    }

    // 如果没有原生桥接，抛出错误（WebView环境下不支持直接相机访问）
    throw new Error(t('pages.delivery.detail.photo.nativeIntegrationRequired'));
  }

  /**
   * 将File对象转换为Base64字符串
   * @param file 文件对象
   * @returns Promise<string> Base64字符串
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * 压缩为缩略图（100x100@60%）用于列表显示
   * 复用现有 compressImage 方法，只是参数不同
   * @param file 原始图片文件
   * @returns Promise<File> 压缩后的缩略图文件（约3KB）
   */
  async compressThumbnail(file: File): Promise<File> {
    return this.compressImage(file, {
      maxWidth: 100,
      maxHeight: 100,
      quality: 60,
      enableResize: true
    });
  }

  /**
   * 处理拍照或选择照片
   * @param type 照片类型
   * @param onPhotosUpdate 照片更新回调
   * @param mode 获取照片的模式：'camera' | 'gallery' | 'front-camera'
   * @returns Promise<void>
   */
  async handleTakePhoto(
    type: PositionType,
    file: File,
    onPhotosUpdate: PhotoUpdateCallback,
    tag?: string,
  ): Promise<void> {
    try {
      // 1. 先转 base64 并展示
      const base64Data = await this.fileToBase64(file);
      onPhotosUpdate((prev) => ({
        ...prev,
        [type]: { url: base64Data, objectKey: '' } // objectKey 先留空
      }));

      // 2. 再异步上传
      let objectKey = '';
      try {
        objectKey = (await fileUpload(file, tag)).objectKey;
      } catch (error) {
        objectKey = FILE_UPLOAD_FAILED_KEY;
      }

      // 3. 上传完成后只更新 objectKey，不影响已展示图片
      onPhotosUpdate((prev) => {
        const prevPhoto = prev[type];
        // 只更新 objectKey，保留已展示的 url
        return {
          ...prev,
          [type]: { url: prevPhoto?.url || base64Data, objectKey }
        };
      });
    } catch (error) {
      console.error('Failed to take photo:', error);
      Toast.show({ content: `Failed to take photo, please try again`, position: 'bottom' });
      throw error;
    }
  }

  /**
   * 处理照片默认不上传
   */
  async handlePhotoUnUpload(
      type: PositionType,
      file: File,
      onPhotosUpdate: PhotoUpdateCallback,
      position?: GeoPosition | null,
      source: PhotoSource = 'camera', // 默认为拍照
  ): Promise<void> {
    try {

      // 使用提取的方法将文件转换为base64格式
      const base64Data = await this.fileToBase64(file);

      onPhotosUpdate((prev) => ({
        ...prev,
        [type]: { 
          url: base64Data, 
          objectKey: FILE_UPLOAD_FAILED_KEY,
          latitude: position?.latitude,
          longitude: position?.longitude,
          capturedAt: Date.now(),
          source: source
        }
      }));

    } catch (error) {
      console.error('Failed to take photo:', error);
      Toast.show({ content: `Failed to take photo, please try again`, position: 'bottom' });
      throw error;
    }
  }

  /**
   * 处理删除照片
   * @param type 照片类型
   * @param onPhotosUpdate 照片更新回调
   * @returns Promise<void>
   */
  async handleDeletePhoto(
    type: PositionType,
    onPhotosUpdate: PhotoUpdateCallback
  ): Promise<void> {
    onPhotosUpdate((prev) => {
      const newState = { ...prev };
      delete newState[type];
      return newState;
    });
    Toast.show({ content: i18n.t('pages.delivery.failed.deleted'), position: 'bottom' });
  }
}

const photoService = new PhotoService();
export default photoService; 